<script setup lang="ts">
import {computed} from "vue";
import {CalendarClock, Layers, Pin, PinOff, Trash2, TriangleAlert, User} from "@lucide/vue";
import {Badge} from "@/core/components/ui/badge";
import {Button} from "@/core/components/ui/button";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";
import {colorForLevel, labelForLevel, textForLevel} from "@/agenda/lib/frequentation";
import {NATURE_LABELS, REASON_LABELS, STATUS_LABELS} from "@/agenda/lib/healthJourney";
import {HealthJourneyStep} from "@/agenda/interfaces/healthJourney";

/**
 * One act of the journey, with what put it where it is.
 *
 * The reasons are shown and not summarised away: a date the pharmacist is
 * about to commit a patient to is one they have to be able to contest, and
 * « pourquoi pas plus tôt » is the first question the counter gets asked.
 */
const {step, actLabel, programName, reasonText, memberName, opensVisit, sharesVisit} = defineProps<{
    step: HealthJourneyStep;
    actLabel: string;
    programName: string | null;
    /** The assistant's sentence for this act, absent when wording was skipped. */
    reasonText: string | null;
    memberName: string | null;
    /** Whether this act is the first of its visit, hence carries its moment. */
    opensVisit: boolean;
    /** Whether the visit holds other acts, which is the point of combining. */
    sharesVisit: boolean;
}>();

const emits = defineEmits<{
    (e: 'edit'): void;
    (e: 'remove'): void;
    (e: 'unpin'): void;
}>();

const dateLabel = computed(() => step.date ? parseAgendaDate(step.date).format('ddd D MMM') : null);

/** Codes this build knows how to say; the others carry nothing readable. */
const codes = computed(() => step.reason_codes
    .map((code) => REASON_LABELS[code])
    .filter((label): label is string => !!label));

const conflicted = computed(() => step.reason_codes.includes('etape_figee_en_conflit'));

const level = computed(() => step.reason.footfall_level);
</script>

<template>
  <article
    class="flex gap-3 rounded-lg border p-3"
    :class="step.status === 'placed' ? 'border-slate-200 bg-white' : 'border-orange/40 bg-orange/10'"
  >
    <div class="w-24 shrink-0 border-r border-slate-100 pr-3">
      <template v-if="opensVisit">
        <p class="text-sm font-semibold capitalize text-primary">
          {{ dateLabel ?? '—' }}
        </p>
        <p v-if="step.start" class="text-xs tabular-nums text-gray-500">
          {{ step.start }}<template v-if="step.end"> – {{ step.end }}</template>
        </p>
        <p v-else class="text-xs text-gray-500">
          {{ STATUS_LABELS[step.status] ?? step.status }}
        </p>
      </template>
      <p v-else class="flex items-center gap-1 text-xs text-gray-500">
        <Layers class="size-3.5" />
        Même visite
      </p>
    </div>

    <div class="min-w-0 flex-1">
      <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
        <p class="text-sm font-medium">
          {{ actLabel }}
        </p>
        <Badge variant="secondary" class="font-normal">
          {{ NATURE_LABELS[step.nature] }}
        </Badge>
        <span class="text-xs text-gray-500">{{ step.duration_minutes }} min</span>
        <Badge v-if="step.pinned" variant="outline" class="gap-1 font-normal">
          <Pin class="size-3" />
          Figé
        </Badge>
        <Badge v-if="step.status !== 'placed'" variant="outline" class="font-normal">
          {{ STATUS_LABELS[step.status] ?? step.status }}
        </Badge>
      </div>

      <p v-if="programName" class="mt-0.5 text-xs text-gray-500">
        {{ programName }}
      </p>

      <p v-if="reasonText" class="mt-1 text-xs leading-snug text-gray-dark">
        {{ reasonText }}
      </p>

      <div class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        <span v-if="memberName" class="flex items-center gap-1">
          <User class="size-3.5" />
          {{ memberName }}
        </span>
        <span v-else-if="step.status === 'placed'" class="flex items-center gap-1">
          <User class="size-3.5" />
          Sans collaborateur
        </span>
        <span
          v-if="level !== null"
          class="flex items-center gap-1 rounded-full px-2 py-0.5"
          :style="{backgroundColor: colorForLevel(level), color: textForLevel(level)}"
        >
          {{ labelForLevel(level) }}
        </span>
        <span v-if="step.reason.regulatory_min_days !== null" class="flex items-center gap-1">
          <CalendarClock class="size-3.5" />
          {{ step.reason.regulatory_min_days }} j réglementaires<template v-if="step.reason.regulatory_source">
            — {{ step.reason.regulatory_source }}
          </template>
        </span>
        <span v-for="code in codes" :key="code">· {{ code }}</span>
        <span v-if="sharesVisit && !opensVisit">· regroupé avec le rendez-vous précédent</span>
      </div>

      <p
        v-if="conflicted"
        class="mt-1.5 flex items-start gap-1.5 rounded-lg bg-orange/20 px-2.5 py-1.5 text-xs leading-snug text-gray-dark"
      >
        <TriangleAlert class="mt-px size-3.5 shrink-0 text-orange" />
        Ce moment a été conservé tel que vous l'avez choisi, bien qu'il ne respecte pas toutes les règles.
      </p>
    </div>

    <div class="flex shrink-0 flex-col gap-1">
      <Button variant="ghost" size="sm" @click="emits('edit')">
        Modifier
      </Button>
      <Button v-if="step.pinned" variant="ghost" size="sm" class="gap-1" @click="emits('unpin')">
        <PinOff class="size-3.5" />
        Libérer
      </Button>
      <Button variant="ghost" size="icon" aria-label="Retirer ce rendez-vous" @click="emits('remove')">
        <Trash2 class="size-4" />
      </Button>
    </div>
  </article>
</template>
